import * as Logger from "js-logger";
import { Signal } from "signals";
import { Exit } from "slotworks/state-machine/data/exit";
import { LINK_TYPE } from "slotworks/state-machine/data/link";
import Node from "slotworks/state-machine/data/node";
import Decision from "slotworks/state-machine/decisions/decision";
import State from "slotworks/state-machine/states/state";
import SuperState from "slotworks/state-machine/states/superstate";

export class StateMachine {

    private static INFINITE_DECISION_THRESHOLD: number = 10;

    public superState: CurrentSuperState = null;
    public currentState: CurrentState = null;

    public onStateEnter: Signal = new Signal();
    public onStateExit: Signal = new Signal();

    /**
     * Pending super states are super states awaiting a complete confirmation after an exit has been reached.
     * Pending states are the state which is waiting to be entered upon the completion of the pending super state
     */
    private pendingSuperState: CurrentSuperState;
    private pendingState: CurrentState;

    /**
     * Sets the current state. Usuaully either on init or by tests
     *
     * @param node {Node}
     */
    public setCurrentState(node: Node) {
        this.currentState = new CurrentState(node, node.concrete as State);

        this.enterState(this.currentState.node);
    }

    /**
     * Sets the current super state. Usuaully either on init or by tests
     *
     * @param node {Node}
     */
    public setSuperState(node: Node) {
        this.superState = new CurrentSuperState(node, node.concrete as SuperState);
    }

    /**
     * Handles requests to change state coming from the active state or super state
     * Super states will intercept requests coming from regular states while they are active
     *
     * @param exit {string | Exit}
     * @param target {State | SuperState}
     */
    public changeState(exit: string | Exit, target: State | SuperState) {
        const invalidState = target !== this.currentState.concrete;
        const invalidSuperState = (!this.superState || target !== this.superState.concrete);
        const invalidPendingSuperState = (!this.pendingSuperState || target !== this.pendingSuperState.concrete);
        if (invalidState && invalidSuperState && invalidPendingSuperState) {
            throw new Error("StateMachine :: A non-active state is trying to change the current state! ");
        }

        if (this.pendingSuperState && target === this.pendingSuperState.concrete) {
            this.log("[SUPER STATE] pending super state resolved");
            this.pendingSuperState = null;

            if (this.pendingState) {
                this.log("[STATE] entering pending state");
                const pendingStateNode = this.pendingState.node;
                this.pendingState = null;
                this.enterState(pendingStateNode);
            }
        } else if (this.superState === null || target === this.superState.concrete) {
            this.exitState(exit);
        } else {
            this.interceptSubStateExit(exit);
        }
    }

    /**
     * Request that the current state (or super state) skips
     */
    public skip(cascade?: boolean) {
        if (this.superState) {
            this.log("[SUPER STATE] skip => " + this.superState.node.name);
            this.superState.concrete.onSkip(cascade);
        } else {
            this.log("[STATE] skip => " + this.currentState.node.name);
            this.currentState.concrete.onSkip(cascade);
        }
    }

    /**
     * Exits the current state, resolves the next one, the enters it
     *
     * @param exit {string | Exit}
     */
    private exitState(exit: string | Exit) {
        let cascadeSkip: boolean = false;

        // Sub states are skipped when a super state is skipped or collapsed
        if (this.superState && (exit === Exit.Skip || exit === Exit.Collapse)) {
            this.currentState.concrete.onSkip();
        }

        this.log("[STATE] exit => " + this.currentState.node.name);

        this.onStateExit.dispatch(this.currentState.node.name);
        this.currentState.concrete.onExit();

        let nextStateNode: Node;

        // Repeats just re-enter the same state, so don't move on
        if (exit === Exit.Repeat) {
            nextStateNode = this.currentState.node;
        } else if (exit === Exit.Collapse) {
            nextStateNode = this.exitSuperState(Exit.Collapse);
        } else {
            cascadeSkip = this.currentState.node.cascadeSkip && (exit === Exit.Skip);

            const nextLink = this.currentState.node.getLink(exit);

            if (nextLink.type === LINK_TYPE.Node) {
                nextStateNode = this.resolveDecision(nextLink.node, 0);
            } else {
                nextStateNode = this.exitSuperState(nextLink.id);
            }
        }

        this.enterState(nextStateNode, cascadeSkip);

        if (this.currentState.node === nextStateNode && cascadeSkip) {
            this.skip(true);
        }
    }

    /**
     * Notify the super state when a sub state exits, the super state then may or may not bubble this event up to the state machine
     *
     * @param exit {string | Exit}
     */
    private interceptSubStateExit(exit: string | Exit) {
        this.log("[SUPER STATE] intercepting => " + exit);

        const superStateConcrete = this.superState.concrete;

        switch (exit) {
            case Exit.Complete:
                superStateConcrete.onSubStateComplete();
                break;
            case Exit.Error:
                superStateConcrete.onSubStateError();
                break;
            default:
                break;
        }
    }

    /**
     * Exits the current super state, making super state null and links to the
     * next state based on the exit id
     *
     * @param exitId {string}
     */
    private exitSuperState(exitId: string | Exit.Collapse): Node {
        if (!this.superState) {
            throw new Error("Missing state node link > " + exitId);
        }

        this.log("[SUPER STATE] exit => " + this.superState.node.name);

        const nextState = this.superState.node.getLink(exitId).node;

        const superStateConcrete = this.superState.concrete;

        this.pendingSuperState = this.superState;
        this.superState = null;

        this.onStateExit.dispatch(this.pendingSuperState.node.name);
        superStateConcrete.onExit();

        return this.resolveDecision(nextState);
    }

    /**
     * Enters the concrete class of current state node.
     * If the current state is a super state, it's sub state entry is also entered
     */
    private enterState(nextStateNode: Node, cascadeSkip: boolean = false) {
        if (nextStateNode.concrete instanceof SuperState) {
            this.log("[SUPER STATE] enter => " + nextStateNode.name);

            this.superState = new CurrentSuperState(nextStateNode, nextStateNode.concrete);
            this.onStateEnter.dispatch(this.superState.node.name);
            this.superState.concrete.onEnter(cascadeSkip);

            const subState = this.resolveDecision(this.superState.node.entrySubState);
            this.currentState = new CurrentState(subState, subState.concrete as State);

        } else {
            this.currentState = new CurrentState(nextStateNode, nextStateNode.concrete as State);
        }

        if (this.superState) {
            this.superState.concrete.subState = this.currentState.concrete;
        }

        if (!this.pendingSuperState) {
            this.log("[STATE] enter => " + this.currentState.node.name);
            this.onStateEnter.dispatch(this.currentState.node.name);
            this.currentState.concrete.onEnter(cascadeSkip);
        } else {
            this.pendingState = this.currentState;
            this.log("[SUPER STATE] waiting for pending super state to complete");
        }
    }

    /**
     * Starting from a node, recursively checks decisions and follows their links until a state (or super state) is found
     * Will throw an exception if an infinite loop is encountered
     *
     * @param node {Node}
     * @param depth [number=0]
     */
    private resolveDecision(node: Node, depth: number = 0): Node {
        if (depth > StateMachine.INFINITE_DECISION_THRESHOLD) {
            throw new Error("StateMachine :: Infinite decision loop detected");
        }

        if (node.concrete instanceof Decision) {
            const decisionResult = node.concrete.evaluate();

            this.log("[DECISION] " + node.name + " => " + (decisionResult === Exit.True));

            const link = node.getLink(decisionResult);

            if (link.type === LINK_TYPE.Alias) {
                return this.exitSuperState(link.id);
            }

            depth++;
            node = this.resolveDecision(link.node, depth);
        }

        return node;
    }

    /**
     * Log a message with colored label
     *
     * @param message {string}
     */
    private log(message: string) {
        Logger.info("%c State Machine ", "background: #00f; color: #fff", message);
    }
}

class CurrentSuperState {
    public concrete: SuperState;
    public node: Node;

    constructor(node: Node, concrete: SuperState) {
        this.node = node;
        this.concrete = concrete;
    }
}

class CurrentState {
    public concrete: State;
    public node: Node;

    constructor(node: Node, concrete: State) {
        this.node = node;
        this.concrete = concrete;
    }
}

export default new StateMachine();



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/state-machine/state-machine.ts